import {
  base,
  baseSepolia,
  chiliz,
  gnosis,
  polygon,
  polygonAmoy,
  spicy,
  arbitrum,
  optimismSepolia,
  optimism,
  type Chain,
} from 'viem/chains'
import { type ChainId } from '@azuro-org/toolkit'

import { type IconName } from 'components/ui'
import { CLASSIC_WEBSITE_URL } from 'src/config/env'

// Extend ChainId type to include additional chains not supported by azuro toolkit
export type ExtendedChainId =
  | ChainId
  | typeof arbitrum.id
  | typeof optimism.id
  | typeof optimismSepolia.id

const companyName = 'DGBet'
const baseUrl = process.env.NEXT_PUBLIC_BASE_URL as string
const privyAppId = process.env.NEXT_PUBLIC_PRIVY_APP_ID as string
const mtPelerinToken = process.env.NEXT_PUBLIC_MTPERELIN_TOKEN as string

// Primary RPC URLs for each chain
const primaryRpcByChains: Record<ExtendedChainId, string> = {
  [gnosis.id]: 'https://gnosis-rpc.publicnode.com',
  [polygon.id]: 'https://polygon-rpc.com',
  [polygonAmoy.id]: 'https://rpc-amoy.polygon.technology',
  [chiliz.id]: 'https://chiliz-rpc.publicnode.com',
  [spicy.id]: 'https://chiliz-spicy-rpc.publicnode.com',
  [base.id]: 'https://base-rpc.publicnode.com',
  [baseSepolia.id]: 'https://base-sepolia-rpc.publicnode.com',
  [arbitrum.id]: 'https://arb1.arbitrum.io/rpc',
  [optimism.id]: 'https://mainnet.optimism.io',
  [optimismSepolia.id]: 'https://optimism-sepolia-rpc.publicnode.com',
} as const

// Backup RPC URLs for fallback functionality
const backupRpcByChains: Record<ExtendedChainId, string[]> = {
  [gnosis.id]: [
    'https://gnosis-mainnet.g.alchemy.com/v2/SS4oxHXbatW7oZ2XQgsbhTbSr9sG_MLc',
  ],
  [polygon.id]: [
    'https://polygon-mainnet.g.alchemy.com/v2/SS4oxHXbatW7oZ2XQgsbhTbSr9sG_MLc',
  ],
  [polygonAmoy.id]: ['https://polygon-amoy.public.blastapi.io'],
  [chiliz.id]: [
    'https://rpc.chiliz.com',
    'https://chiliz-mainnet.public.blastapi.io',
    'https://rpc.ankr.com/chiliz',
  ],
  [spicy.id]: ['https://spicy-rpc.chiliz.com'],
  [base.id]: [
    'https://base-mainnet.g.alchemy.com/v2/SS4oxHXbatW7oZ2XQgsbhTbSr9sG_MLc',
  ],
  [baseSepolia.id]: [
    'https://sepolia.base.org',
    'https://base-sepolia.public.blastapi.io',
  ],
  [arbitrum.id]: [
    'https://arb-mainnet.g.alchemy.com/v2/SS4oxHXbatW7oZ2XQgsbhTbSr9sG_MLc',
  ],
  [optimism.id]: [
    'https://opt-mainnet.g.alchemy.com/v2/SS4oxHXbatW7oZ2XQgsbhTbSr9sG_MLc',
  ],
  [optimismSepolia.id]: [
    'https://sepolia.optimism.io',
    'https://optimism-sepolia.public.blastapi.io',
  ],
} as const

// Cache for working RPC URLs
const workingRpcCache = new Map<ExtendedChainId, string>()

/**
 * Test if an RPC URL is working
 */
const testRpcUrl = async (
  url: string,
  timeoutMs: number = 5000
): Promise<boolean> => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'eth_blockNumber',
        params: [],
        id: 1,
      }),
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (response.ok) {
      const data = await response.json()
      return !!data.result
    }
    return false
  } catch (error) {
    return false
  }
}

/**
 * Get all available RPC URLs for a chain (primary + backups)
 */
const getAllRpcUrls = (chainId: ExtendedChainId): string[] => {
  const primary = primaryRpcByChains[chainId]
  const backups = backupRpcByChains[chainId] || []
  return [primary, ...backups]
}

/**
 * Find the first working RPC URL for a chain
 */
const findWorkingRpcUrl = async (chainId: ExtendedChainId): Promise<string> => {
  const urls = getAllRpcUrls(chainId)

  if (!urls || urls.length === 0) {
    throw new Error(`No RPC URLs configured for chain ${chainId}`)
  }

  // Try each URL in sequence
  for (const url of urls) {
    try {
      const isWorking = await testRpcUrl(url)
      if (isWorking) {
        return url
      }
    } catch (error) {
      continue
    }
  }

  return urls[0]
}

/**
 * Get RPC URL for a chain - returns cached working URL or finds a new one
 */
const getRpcUrl = async (chainId: ExtendedChainId): Promise<string> => {
  // Check cache first
  if (workingRpcCache.has(chainId)) {
    const cachedUrl = workingRpcCache.get(chainId)!
    // Test if cached URL is still working
    const isStillWorking = await testRpcUrl(cachedUrl)
    if (isStillWorking) {
      return cachedUrl
    }
    // Remove from cache if not working
    workingRpcCache.delete(chainId)
  }

  // Find working URL and cache it
  const workingUrl = await findWorkingRpcUrl(chainId)
  workingRpcCache.set(chainId, workingUrl)
  return workingUrl
}

/**
 * Synchronous getter for RPC URLs - returns cached working URL or primary
 */
export const rpcByChains: Record<ExtendedChainId, string> = {
  [gnosis.id]: primaryRpcByChains[gnosis.id],
  [polygon.id]: primaryRpcByChains[polygon.id],
  [polygonAmoy.id]: primaryRpcByChains[polygonAmoy.id],
  [chiliz.id]: primaryRpcByChains[chiliz.id],
  [spicy.id]: primaryRpcByChains[spicy.id],
  [base.id]: primaryRpcByChains[base.id],
  [baseSepolia.id]: primaryRpcByChains[baseSepolia.id],
  [arbitrum.id]: primaryRpcByChains[arbitrum.id],
  [optimism.id]: primaryRpcByChains[optimism.id],
  [optimismSepolia.id]: primaryRpcByChains[optimismSepolia.id],
} as const

/**
 * Get the current working RPC URL for a chain
 * This function will return the cached working URL or the primary URL
 */
export const getCurrentRpcUrl = (chainId: ExtendedChainId): string => {
  // First try to get from cache
  const cachedUrl = workingRpcCache.get(chainId)
  if (cachedUrl) {
    return cachedUrl
  }

  // If no cached URL, try to get from backup URLs first
  const backupUrls = backupRpcByChains[chainId] || []
  if (backupUrls.length > 0) {
    return backupUrls[0]
  }
  return primaryRpcByChains[chainId]
}

/**
 * Initialize RPC URLs by testing and caching working ones
 */
export const initializeRpcUrls = async (): Promise<void> => {
  const chainIds: ExtendedChainId[] = [
    gnosis.id,
    polygon.id,
    polygonAmoy.id,
    chiliz.id,
    spicy.id,
    base.id,
    baseSepolia.id,
    arbitrum.id,
    optimism.id,
    optimismSepolia.id,
  ]

  // Test all chains in parallel
  const promises = chainIds.map(async chainId => {
    try {
      const workingUrl = await findWorkingRpcUrl(chainId)
      workingRpcCache.set(chainId, workingUrl)
    } catch (error) {}
  })

  await Promise.all(promises)
}

/**
 * Refresh RPC URL for a specific chain
 */
export const refreshRpcUrl = async (
  chainId: ExtendedChainId
): Promise<string> => {
  workingRpcCache.delete(chainId)
  return await getRpcUrl(chainId)
}

/**
 * Get working RPC URL for a chain (async version)
 */
export const getWorkingRpcUrl = getRpcUrl

export const chainIcons: Record<ExtendedChainId, IconName> = {
  [gnosis.id]: 'networks/gnosis',
  [polygon.id]: 'networks/polygon',
  [polygonAmoy.id]: 'networks/polygon',
  [chiliz.id]: 'networks/chiliz',
  [spicy.id]: 'networks/chiliz',
  [base.id]: 'networks/base',
  [baseSepolia.id]: 'networks/base',
  [arbitrum.id]: 'networks/arbitrum',
  [optimism.id]: 'networks/op',
  [optimismSepolia.id]: 'networks/op',
}

export const currencyIcons: Record<ExtendedChainId, IconName> = {
  [gnosis.id]: 'currency/wxdai',
  [polygon.id]: 'currency/usdt',
  [polygonAmoy.id]: 'currency/azusd',
  [chiliz.id]: 'currency/wchz',
  [spicy.id]: 'currency/wchz',
  [base.id]: 'currency/weth',
  [baseSepolia.id]: 'currency/weth',
  [arbitrum.id]: 'currency/usdc',
  [optimism.id]: 'currency/usdc',
  [optimismSepolia.id]: 'currency/usdc',
}

export const chainByChainId: Record<number, Chain> = {
  [optimismSepolia.id]: optimismSepolia,
  [optimism.id]: optimism,
  [polygon.id]: polygon,
  [gnosis.id]: gnosis,
  [chiliz.id]: chiliz,
  [base.id]: base,
  [arbitrum.id]: arbitrum,
  [baseSepolia.id]: baseSepolia,
  [polygonAmoy.id]: polygonAmoy,
  [spicy.id]: spicy,
}

const OVERTIME_CHAIN_IDS = new Set<number>([
  arbitrum.id,
  optimismSepolia.id,
  optimism.id,
  base.id,
  baseSepolia.id,
])

// const SX_BET_CHAIN_IDS = new Set<number>([berachain.id])

const AZURO_CHAIN_IDS = new Set<number>([
  polygon.id,
  polygonAmoy.id,
  gnosis.id,
  chiliz.id,
  spicy.id,
  base.id,
  baseSepolia.id,
])

const sportsOrder = [
  'politics',
  'football',
  'basketball',
  'tennis',
  'cricket',
  'mma',
  'boxing',
  'ice-hockey',
  'american-football',
  'baseball',
  'rugby-union',
  'rugby-league',
]

const links = {
  about: 'https://dgbet.fun/about-us/',
  blog: 'https://dgbet.fun/blog/',
  terms: 'https://dgbet.fun/terms-and-conditions/',
  policy: 'https://dgbet.fun/privacy-policy/',
  rulesAndDisclaimers: 'https://dgbet.fun/rules-disclaimers/',
  waves: 'https://azuro.org/app/waves',
}

const navigationLinks = {
  profile: '/profile',
  promotions: 'https://dgbet.fun/contests/',
  affiliate: 'https://dgbet.fun/affiliates/',
  whitepaper: 'https://dgbet.gitbook.io/whitepaper',
  blog: 'https://dgbet.fun/blog/',
  about: 'https://dgbet.fun/about-us/',
  classic: CLASSIC_WEBSITE_URL || '/',
}

const socialLinks = {
  instagram: 'https://www.instagram.com/dgbet_official_/#',
  telegram: 'https://t.me/+ID8zItSE9QJjOTM1',
  twitter: 'https://x.com/dgbet_official',
  discord: 'https://discord.com/invite/VeGrqdNKU5',
}

const defaultSlippageValues = ['5', '10', '15']
const defaultQuickBetsValues = ['50', '100']

const localStorageKeys = {
  slippage: 'slippage',
  quickBet: 'quickBet',
  gameMarketsView: 'gameMarketsView',
  collapsedMarkets: 'collapsedMarkets',
  oddsView: 'oddsView',
  timeFormat: 'timeFormat',
  nudgeIsOpen: 'nudgeIsOpen',
  nudgeData: 'nudgeData',
  hasFetchedUserInfo: 'hasFetchedUserInfo',
  invite: 'invite',
  utmParams: 'utmParams',
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmTerm: 'utm_term',
  utmContent: 'utm_content',
  promotionalModal: 'promotional-modal',
  loginMethod: 'loginMethod',
  hasLoggedOut: 'hasLoggedOut',
}

export default {
  baseUrl,
  companyName,
  rpcByChains,
  getCurrentRpcUrl,
  getWorkingRpcUrl,
  initializeRpcUrls,
  refreshRpcUrl,
  chainByChainId,
  mtPelerinToken,
  topPageGamePerSportLimit: 10,
  topPageGamePerSportLimitOvertime: 10000,
  chainIcons,
  currencyIcons,
  sportsOrder,
  links,
  defaultSlippageValues,
  defaultSlippage: '10',
  defaultQuickBetsValues,
  localStorageKeys,
  privyAppId,
  navigationLinks,
  socialLinks,
  OVERTIME_CHAIN_IDS,
  AZURO_CHAIN_IDS,
}
